import { useEffect, useState } from 'react';
import { ArrowRight, FileUp, Folder, History, MessageSquare, Radio, Scale, X } from 'lucide-react';

// P4 Section 4.1. First-login welcome + 5-step tour (critical for new users like
// John). localStorage-gated: shows once, reopenable from Settings (which sets
// cc.onboarding.pending). Plain content, no spotlight overlay — a clear, honest
// walk-through of the five things a new user needs.

const DONE_KEY = 'cc.onboarding.done';
const PENDING_KEY = 'cc.onboarding.pending';

interface Step {
  icon: React.ReactNode;
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    icon: <Folder className="h-5 w-5" />,
    title: '1. Create a matter',
    body: 'A matter holds one case: its background, the folders to search, and the documents you upload. Go to Matters and click New matter. Paste the case context so the AI is grounded in your facts.',
  },
  {
    icon: <Radio className="h-5 w-5" />,
    title: '2. Start a hearing',
    body: 'Pick the matter, then press Start listening (or F5). Your laptop mic transcribes the room live, in the browser. The first time each session you confirm a short lawful-use checklist.',
  },
  {
    icon: <MessageSquare className="h-5 w-5" />,
    title: '3. Ask a question',
    body: 'Tap a fast action (F1-F4) for an instant answer, e.g. "Counter argument". Try: counsel says you defaulted — hit F1 and you get one line on what they said, why it fails, and what to say next.',
  },
  {
    icon: <FileUp className="h-5 w-5" />,
    title: '4. Upload a document',
    body: 'Open a matter and add PDFs, emails, or spreadsheets. The AI reads them and cites them in answers. Do this the night before so the evidence is loaded when you need it.',
  },
  {
    icon: <History className="h-5 w-5" />,
    title: '5. Find past sessions',
    body: 'Every hearing is saved under Sessions: the full transcript, every answer, and a one-click PDF or Word export. Tap Review on a finished session for an honest what-worked / what-to-fix summary.',
  },
];

export function OnboardingTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      const pending = window.localStorage.getItem(PENDING_KEY) === '1';
      const done = window.localStorage.getItem(DONE_KEY) === '1';
      if (pending || !done) {
        setOpen(true);
      }
    } catch {
      // localStorage unavailable — skip the tour rather than block the app.
    }
  }, []);

  function finish(): void {
    try {
      window.localStorage.setItem(DONE_KEY, '1');
      window.localStorage.removeItem(PENDING_KEY);
    } catch {
      // ignore
    }
    setOpen(false);
  }

  if (!open) return null;
  const isWelcome = step === 0;
  const current = STEPS[step - 1];

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 p-4" role="dialog" aria-label="Welcome tour">
      <div className="w-full max-w-md rounded-xl border border-stone-700 bg-stone-900 p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br from-amber-500 to-amber-700">
            {isWelcome ? <Scale className="h-6 w-6 text-stone-950" strokeWidth={2.5} /> : current?.icon}
          </div>
          <button
            type="button"
            onClick={finish}
            aria-label="Skip tour"
            className="rounded p-1 text-stone-500 hover:text-stone-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {isWelcome ? (
          <>
            <h2 className="font-display text-2xl italic text-stone-100">Welcome to Court Copilot</h2>
            <p className="mt-2 text-sm leading-relaxed text-stone-400">
              Your real-time courtroom assistant. It listens, finds your evidence, and tells you what
              to say next, in plain English. Here is the 60 second tour of the five things you need.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-lg text-stone-100">{current?.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-stone-400">{current?.body}</p>
          </>
        )}

        <div className="mt-5 flex items-center justify-between">
          <div className="flex gap-1.5">
            {Array.from({ length: STEPS.length + 1 }).map((_, i) => (
              <span
                key={i}
                className={
                  'h-1.5 w-1.5 rounded-full ' + (i === step ? 'bg-amber-500' : 'bg-stone-700')
                }
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {step > 0 ? (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-stone-300"
              >
                Back
              </button>
            ) : (
              <button
                type="button"
                onClick={finish}
                className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-stone-300"
              >
                Skip
              </button>
            )}
            {step < STEPS.length ? (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 hover:bg-amber-700"
              >
                {isWelcome ? 'Start tour' : 'Next'}
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={finish}
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 hover:bg-amber-700"
              >
                Get started
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
